<template>

  <v-container fluid class="dashboard-stats-shipper-ui">
    <!-- Title and Refresh action -->
    <v-row>
      <v-col cols="12" xs="12" sm="8" md="8" lg="9" class="d-flex align-center">
        <div class="page-title">{{ activeOrg.name }} Stats</div>
      </v-col>
      <v-col cols="12" xs="12" sm="4" md="4" lg="3" class="d-flex align-center justify-center justify-sm-end">
        <div v-if="enableAutoRefresh" class="mr-2" style="font-size:0.9rem;color:#727272;">Auto refresh: {{ refreshFrequency / 1000 }}s</div>
        <v-tooltip top nudge-top="-12">
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon color="primary" class="" @click="loadOrgStats" v-bind="attrs" v-on="on">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </template>
          <span>Refresh data</span>
        </v-tooltip>
      </v-col>
    </v-row>

    <!-- (data loading spinner) -->
    <v-container class="d-flex flex-column align-center justify-center" v-if="isDataLoading">
      <v-progress-circular indeterminate :size="35" style="margin-top:30px" color="primary" />
    </v-container>

    <!-- Org Stats, Active Routes -->
    <template v-else>
      <!---------------->
      <!-- Route Info -->
      <!---------------->
      <v-row>
        <!-- Upcoming Routes -->
        <v-col cols="12" xs="12" sm="4" md="4" lg="4" class="d-flex align-center justify-start">
          <div style="width:380px;" class="d-flex flex-column align-center justify-center page-content">
            <div class="d-flex align-center module-title">
              Upcoming Routes
            </div>
            <!-- Building -->
            <div class="d-flex align-center">
              <div class="module-sub-label mr-2">Building:</div>
              <div class="mr-2">{{ orgCounts.routes_building }}</div>
            </div>
            <!-- Dispatched -->
            <div class="d-flex align-center">
              <div class="module-sub-label mr-2">Dispatched:</div>
              <div class="mr-2">{{ orgCounts.routes_dispatched }}</div>
            </div>
          </div>
        </v-col>

        <!-- Routes on Delivery -->
        <v-col cols="12" xs="12" sm="4" md="4" lg="4" class="d-flex align-center justify-start">
          <div style="width:380px;" class="d-flex flex-column align-center justify-center page-content">
            <div class="d-flex align-center module-title">
              Routes on Delivery
            </div>
            <div class="d-flex align-center">
              <div class="active-count-text">{{ orgCounts.routes_active }}</div>
            </div>
            <div class="d-flex align-center">
              <div class="module-sub-label mr-2">Active</div>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="#727272" v-bind="attrs" v-on="on" style="cursor:pointer;">
                    mdi-information-outline
                  </v-icon>
                </template>
                <div>
                  <div>Statuses:</div>
                  <div>(ON_DELIVERY, RETURNING_TO_HQ)</div>
                </div>
              </v-tooltip>
            </div>
          </div>
        </v-col>

        <!-- Total Routes -->
        <v-col cols="12" xs="12" sm="4" md="4" lg="4" class="d-flex align-center justify-start">
          <div style="width:380px;" class="d-flex flex-column align-center justify-center page-content">
            <div class="d-flex align-center justify-center module-title">
              Total Routes
            </div>
            <div class="d-flex align-center">
              <div class="active-count-text">{{ orgCounts.routes_completed_total }}</div>
              <div class="count-divider">/</div>
              <div class="total-count-text">{{ orgCounts.routes_dispatched_total }}</div>
            </div>
            <div class="d-flex align-center">
              <div class="module-sub-label mr-2">Completed</div>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="#727272" v-bind="attrs" v-on="on" style="cursor:pointer;">
                    mdi-information-outline
                  </v-icon>
                </template>
                <div>Routes completed / total dispatched</div>
              </v-tooltip>
            </div>
          </div>
        </v-col>
      </v-row>

      <!---------------->
      <!-- Order Info -->
      <!---------------->
      <v-row>
        <!-- Upcoming Orders -->
        <v-col cols="12" xs="12" sm="4" md="4" lg="4" class="d-flex align-center justify-start">
          <div style="width:380px;" class="d-flex flex-column align-center justify-center page-content">
            <div class="d-flex align-center module-title">
              Upcoming Orders
            </div>
            <!-- Building -->
            <div class="d-flex align-center">
              <div class="module-sub-label mr-2">Placed:</div>
              <div class="mr-2">{{ orgCounts.routes_building }}</div>
            </div>
            <!-- Dispatched -->
            <div class="d-flex align-center">
              <div class="module-sub-label mr-2">Being Scheduled:</div>
              <div class="mr-2">{{ orgCounts.routes_dispatched }}</div>
            </div>
          </div>
        </v-col>

        <!-- Orders on Delivery -->
        <v-col cols="12" xs="12" sm="4" md="4" lg="4" class="d-flex align-center justify-start">
          <div style="width:380px;" class="d-flex flex-column align-center justify-center page-content">
            <div class="d-flex align-center module-title">
              Orders on Delivery
            </div>
            <div class="d-flex align-center">
              <div class="active-count-text">{{ orgCounts.routes_active }}</div>
            </div>
            <div class="d-flex align-center">
              <div class="module-sub-label mr-2">Active</div>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="#727272" v-bind="attrs" v-on="on" style="cursor:pointer;">
                    mdi-information-outline
                  </v-icon>
                </template>
                <div>
                  <div>Statuses:</div>
                  <div>(ON_DELIVERY, RETURNING_TO_HQ)</div>
                </div>
              </v-tooltip>
            </div>
          </div>
        </v-col>

        <!-- Total Orders -->
        <v-col cols="12" xs="12" sm="4" md="4" lg="4" class="d-flex align-center justify-start">
          <div style="width:380px;" class="d-flex flex-column align-center justify-center page-content">
            <div class="d-flex align-center justify-center module-title">
              Total Orders
            </div>
            <div class="d-flex align-center">
              <div class="active-count-text">{{ orgCounts.routes_completed_total }}</div>
              <div class="count-divider">/</div>
              <div class="total-count-text">{{ orgCounts.routes_dispatched_total }}</div>
            </div>
            <div class="d-flex align-center">
              <div class="module-sub-label mr-2">Delivered</div>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon small color="#727272" v-bind="attrs" v-on="on" style="cursor:pointer;">
                    mdi-information-outline
                  </v-icon>
                </template>
                <div>Orders delivered / total submitted</div>
              </v-tooltip>
            </div>
          </div>
        </v-col>
      </v-row>

      <!-- Active Routes -->
      <v-row class="mt-8">
        <v-col class="d-flex align-center">
          <div class="page-title mr-4">Active Routes</div>
          <div class="page-title-details">(Dispatched, On Delivery)</div>
        </v-col>
      </v-row>
      <v-expansion-panels flat accordion class="mt-4" v-model="data.is_expanded" v-for="data in activeRoutes" :key="`org-${data.org.id}`">
        <v-expansion-panel>
          <v-expansion-panel-header>
            <div class="d-flex align-center">
              <span class="mr-3 active-route-org-label">{{ data.org.name }}</span><span>({{ data.active_routes.length }})</span>
            </div>
          </v-expansion-panel-header>
          <v-expansion-panel-content class="pl-2 pb-4 mt-4">
            <v-row>
              <v-col cols="12" xs="12" sm="3" md="3" lg="4">
                <div class="active-route-field-header">Route name</div>
              </v-col>
              <v-col cols="12" xs="12" sm="3" md="3" lg="3">
                <div class="active-route-field-header">Scheduled for</div>
              </v-col>
              <v-col cols="12" xs="12" sm="3" md="3" lg="3">
                <div class="active-route-field-header">Started delivery</div>
              </v-col>
              <v-col cols="12" xs="12" sm="3" md="3" lg="2">
                <div class="active-route-field-header">Status</div>
              </v-col>
            </v-row>
            <v-row class="pb-2 active-route-record" v-for="route in data.active_routes" :key="`route-${route.id}`">
              <v-col cols="12" xs="12" sm="3" md="3" lg="4" class="d-flex align-center">
                <div class="mr-3">{{ route.name }}</div>
              </v-col>
              <v-col cols="12" xs="12" sm="3" md="3" lg="3" class="d-flex align-center">
                <div>
                  <div class="active-route-date">{{ formatDate(route.scheduled_start_time) }}</div>
                  <div class="active-route-time">{{ formatTime(route.scheduled_start_time) }}</div>
                </div>
                <div class="ml-3" v-if="isRouteDelayed(route)">
                  <!-- (alert on delayed) -->
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                          color="yellow darken-3"
                          style="cursor:pointer;font-size:21px;margin-right:3px;"
                          v-bind="attrs"
                          v-on="on">
                        mdi-alert
                      </v-icon>
                    </template>
                    <div>Route has been delayed beyond {{ delayedThresholdHrs }} hrs.</div>
                  </v-tooltip>
                </div>
              </v-col>
              <v-col cols="12" xs="12" sm="3" md="3" lg="3" class="d-flex align-center">
                <div>
                  <div class="active-route-date">{{ formatDate(route.on_delivery_at) }}</div>
                  <div class="active-route-time">{{ formatTime(route.on_delivery_at) }}</div>
                </div>
                <div class="ml-3">
                  <!-- (alert on stuck) -->
                  <v-tooltip top v-if="isRouteStuck(route)">
                    <template v-slot:activator="{ on, attrs }">
                      <v-icon
                          color="red accent-2"
                          style="cursor:pointer;font-size:21px;margin-right:3px;"
                          v-bind="attrs"
                          v-on="on">
                        mdi-alert
                      </v-icon>
                    </template>
                    <div>Expected completion was more than {{ stuckThresholdHrs }} hrs ago.</div>
                  </v-tooltip>
                </div>
              </v-col>
              <v-col cols="12" xs="12" sm="3" md="3" lg="2" class="d-flex align-center">
                <v-chip small label outlined :class="route.route_status">{{ route.route_status }}</v-chip>
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
  </v-container>

</template>

<script>
/* global moment */
export default {
  name: 'OrgDashboardStatsShipperUi',

  components: {
  },

  data () {
    return {
      isDataLoading: false,
      dataLoadingError: null,
      // ---------- loaded data
      orgStats: null,
      activeRoutes: [],
      // ----------- auth refresh
      enableAutoRefresh: true,
      refreshFrequency: 30 * 1000, // 30 seconds
      autoRefresh: null,
      // ----------- warning settings
      delayedThresholdHrs: 2,
      stuckThresholdHrs: 3
    }
  },

  computed: {
    uiType () {
      return this.$root.uiType()
    },
    activeOrg () {
      return this.$store.getters['auth/activeOrg'] || {}
    },
    activeUser () {
      return this.$store.getters['auth/activeUser']
    },
    userSettings () {
      return this.$store.getters['app/settings']
    },
    orgCounts () {
      return this.orgStats?.counts || {}
    }
  },

  beforeMount () {
    this.loadOrgStats()
  },

  mounted () {
    if (this.enableAutoRefresh) {
      this.autoRefresh = setInterval(() => {
        this.loadOrgStats()
      }, this.refreshFrequency)
    }
  },

  beforeDestroy () {
    // Clean up polling interval when component is destroyed
    if (this.autoRefresh) {
      clearInterval(this.autoRefresh)
      this.autoRefresh = null
    }
  },

  methods: {
    // Display a date
    formatDate (date) {
      return (date) ? this.$root.utils.format(date, 'MMM D, YYYY') : ''
    },

    // Display a time
    formatTime (time) {
      return (time) ? this.$root.utils.format(time, 'h:mm a') : ''
    },

    // Determine if route is delayed beyond its expected departure
    isRouteDelayed (route = {}) {
      if (route.on_delivery_at) return false
      const expectedStart = route.scheduled_start_time
      const routeTime = moment(expectedStart).tz(this.userSettings.timezone)
      const now = moment().tz(this.userSettings.timezone)
      return now.diff(routeTime, 'hours') >= this.delayedThresholdHrs
    },

    // Determine if route is stuck beyond its expected completion
    isRouteStuck (route = {}) {
      if (!route.on_delivery_at) return false
      const expectedEnd = route.estimated_return_time
      const routeTime = moment(expectedEnd).tz(this.userSettings.timezone)
      const now = moment().tz(this.userSettings.timezone)
      return now.diff(routeTime, 'hours') > this.stuckThresholdHrs
    },

    // Build active route data for display
    buildActiveRoutes () {
      this.activeRoutes = []
      // const activeRouteData = this.orgStats?.active_routes || {}
      // this.activeRoutes = Object.keys(activeRouteData).map(orgId => {
      //   return {
      //     org: this.orgStats.shipper_orgs.data.find(it => it.id === orgId),
      //     active_routes: activeRouteData[orgId]
      //   }
      // })
    },

    // Load org stats data
    async loadOrgStats () {
      this.isDataLoading = true

      try {
        this.orgStats = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'org/counts',
          opts: {
            params: {
              counts: [
                'routes_active',
                'routes_building',
                'routes_dispatched',
                'routes_completed',
                'routes_canceled'
              ]
            }
          },
          to_model: false
        })

        this.buildActiveRoutes()
      } catch (err) {
        this.orgStats = {}
        this.dataLoadingError = err.message || err.text || err
        console.log('Error retrieving org stats =>', err)
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style scoped>
  .dashboard-stats-shipper-ui .page-title-details {
    color: #9c9c9c;
    font-size: 1.2rem;
  }

  /* Dashboard modules */
  .dashboard-stats-shipper-ui .page-content {
    width: 260px;
    padding-left: 24px;
    padding-right: 24px;
  }

  .dashboard-stats-shipper-ui .module-title {
    color: #9c9c9c;
    font-size: 1.0rem;
  }

  .dashboard-stats-shipper-ui .module-sub-label {
    text-transform: uppercase !important;
    font-size: 0.9rem;
  }

  .dashboard-stats-shipper-ui .active-count-text {
    font-size: 2.9rem;
  }

  .dashboard-stats-shipper-ui .count-divider {
    color: #3c3c3c;
    font-size: 1.5rem;
    padding-top: 12px;
    margin-left: 4px;
    margin-right: 2px;
  }

  .dashboard-stats-shipper-ui .total-count-text {
    color: #727272;
    font-size: 1.3rem;
    padding-top: 14px;
  }

  /* Active Routes */
  .dashboard-stats-shipper-ui .active-route-org-label {
    font-size: 1.18rem;
  }

  .dashboard-stats-shipper-ui .active-route-field-header {
    color: #727272;
    font-size: 0.9rem;
  }

  .dashboard-stats-shipper-ui .active-route-date {
    color: #9c9c9c;
  }

  .dashboard-stats-shipper-ui .active-route-time {
    /* color: #727272; */
  }

  .dashboard-stats-shipper-ui .theme--dark.v-expansion-panels .v-expansion-panel {
    background-color: #161616;
    border: 1px solid #262626;
  }

  .dashboard-stats-shipper-ui .v-expansion-panel--active>.v-expansion-panel-header {
    min-height: unset;
  }

  .dashboard-stats-shipper-ui .v-expansion-panel-content__wrap {
    padding-left: 0px;
  }

  .dashboard-stats-shipper-ui .v-expansion-panel-content .active-route-record {
    border-bottom: 1px solid #252525;
  }

  .dashboard-stats-shipper-ui .v-expansion-panel-content .active-route-record:last-of-type {
    border-bottom: none !important;
  }
</style>
